import { Form, Input, Select, DatePicker, ConfigProvider, Switch, Button } from "antd";
import { Article } from "@/services/Articles/updateArticle";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import localizedFormat from "dayjs/plugin/localizedFormat";
import trTR from "antd/locale/tr_TR";
import { PlusOutlined } from "@ant-design/icons";
import { ContentEditor } from "./ContentEditor";

dayjs.extend(localizedFormat);
dayjs.locale("tr");

interface ArticleUpdateFormPropsI {
    article: Article;
    onSubmit?: (values: any) => void; // form verilerini dışarıya iletmek için
    className: string;
}

export const ArticleUpdateForm = ({ article, onSubmit, className }: ArticleUpdateFormPropsI) => {
    const [form] = Form.useForm();
    return (
        <div
            className={className}
        >
            <ConfigProvider locale={trTR}>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        ...article,
                        isPublic: article.isPublic,  // boolean garantisi
                        isPinned: !!article.isPinned,  // boolean garantisi
                        createdDate: article.createdDate ? dayjs(article.createdDate) : undefined,
                        updatedDate: article.updatedDate ? dayjs(article.updatedDate) : undefined,
                    }}
                    onValuesChange={(_, allValues) => {
                        // Gelen tüm form verilerini al
                        const processedValues = {
                            ...allValues,
                            createdDate: allValues.createdDate?.toISOString(),  // dayjs → ISO string
                            updatedDate: allValues.updatedDate?.toISOString(),
                            // 'relatedArticles' metin dizisi olarak gelir, bunu sayı dizisine çevir
                            relatedArticles: (allValues.relatedArticles || [])
                                .map((id: any) => {
                                    if (typeof id === 'string') {
                                        return Number(id.trim());
                                    }
                                    if (typeof id === 'number') {
                                        return id;
                                    }
                                    return NaN;
                                })
                                .filter((id: number) => !isNaN(id) && id > 0),
                            featured: (allValues.featured || []).map((f: any) => ({
                                type: f?.type || "",
                                title: f?.title || "",
                                items: f?.items || [],
                            })),
                        };
                        // Üst bileşene (Read/View) işlenmiş ve temiz veriyi gönder
                        onSubmit?.(processedValues);
                    }}
                >
                    <div
                        style={{ display: 'flex', gap: '20px', }}
                    >
                        <Form.Item name="isPublic" valuePropName="checked" label="YAYINLA">
                            <Switch />
                        </Form.Item>

                        {/* Pinned Switch */}
                        <Form.Item name="isPinned" valuePropName="checked" label="PİNLE">
                            <Switch />
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="Slug"
                        name="slug"
                        rules={[
                            { required: true, message: "Slug gerekli" },
                            {
                                validator: (_, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 5) return Promise.reject(new Error("Slug en az 5 karakter olmalı"));
                                    if (value.length > 50) return Promise.reject(new Error("Slug en fazla 50 karakter olmalı"));
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Resim URL"
                        name="imgUrl"
                        rules={[
                            { required: true, message: "Resim URL gerekli" },
                            {
                                validator: (_, value: string) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 10) return Promise.reject(new Error("URL en az 10 karakter olmalı"));
                                    if (value.length > 200) return Promise.reject(new Error("URL en fazla 200 karakter olmalı"));
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>


                    <Form.Item
                        label="Başlık"
                        name="title"
                        rules={[
                            { required: true, message: "Başlık gerekli" },
                            {
                                validator: (_, value: string) => {
                                    if (!value) return Promise.resolve(); // required zaten kontrol ediyor
                                    if (value.length < 10) {
                                        return Promise.reject(new Error("Başlık en az 10 karakter olmalı"));
                                    }
                                    if (value.length > 100) {
                                        return Promise.reject(new Error("Başlık en fazla 100 karakter olmalı"));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Kategori"
                        name="category"
                        rules={[
                            { required: true, message: "Kategori gerekli" },
                            {
                                validator: (_, value: string) => {
                                    if (!value) return Promise.resolve(); // required zaten kontrol ediyor
                                    if (value.length < 3) {
                                        return Promise.reject(new Error("Kategori en az 3 karakter olmalı"));
                                    }
                                    if (value.length > 20) {
                                        return Promise.reject(new Error("Kategori en fazla 20 karakter olmalı"));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Oluşturulma Tarihi"
                        name="createdDate"
                        rules={[{ required: true, message: "Oluşturulma tarihi gerekli" }]}
                    >

                        <DatePicker
                            showTime
                            style={{ width: "100%" }}
                            format="DD MMMM YYYY HH:mm"
                            placeholder="Oluşturulma tarihi seçin"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Güncellenme Tarihi"
                        name="updatedDate"
                        rules={[{ required: true, message: "Güncellenme tarihi gerekli" }]}
                    >

                        <DatePicker
                            showTime
                            style={{ width: "100%" }}
                            format="DD MMMM YYYY HH:mm"
                            placeholder="Güncellenme tarihi seçin"
                        />

                    </Form.Item>

                    <Form.Item
                        label="Etiketler"
                        name="tags"
                        rules={[
                            {
                                validator: (_, value: string[]) => {
                                    if (!value || value.length === 0) {
                                        return Promise.reject(new Error("En az 1 etiket eklemelisiniz"));
                                    }
                                    // Maksimum 5 etiket
                                    if (value.length > 10) return Promise.reject(new Error("En fazla 10 etiket ekleyebilirsiniz"));
                                    // Her etiket en az 3 ve en fazla 10 karakter
                                    if (value.some(tag => tag.length < 3)) return Promise.reject(new Error("Her etiket en az 3 karakter olmalı"));
                                    if (value.some(tag => tag.length > 20)) return Promise.reject(new Error("Her etiket en fazla 20 karakter olabilir"));
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Select
                            mode="tags"
                            style={{ width: "100%" }}
                            placeholder="Etiket ekle"
                        />
                    </Form.Item>

                    <Form.Item
                        label="İlgili Makaleler (ID)"
                        name="relatedArticles"
                        rules={[
                            {
                                validator: (_, value: number[]) => {
                                    if (!value || value.length < 3) {
                                        return Promise.reject(new Error("En az 3 ilgili makale eklemelisiniz"));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Select
                            mode="tags"
                            style={{ width: "100%" }}
                            placeholder="ID ekle"

                        />
                    </Form.Item>

                    <Form.List name="featured">
                        {(fields, { add, remove }) => (
                            <div>
                                <label>Featured Alanlar</label>
                                {fields.map(({ key, name, fieldKey, ...restField }) => (
                                    <div
                                        key={key}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            padding: '12px',       // arkaplan içi boşluk
                                            marginBottom: '8px',   // alanlar arası boşluk
                                            background: '#f5f5f5', // açık gri arkaplan
                                            borderRadius: '6px',   // köşeleri yuvarlat
                                        }}
                                    >
                                        <i
                                            className="fi fi-sr-square-minus"
                                            onClick={() => remove(name)}
                                            style={{ alignSelf: 'flex-end', cursor: 'pointer', marginBottom: '8px' }}
                                        ></i>

                                        <Form.Item
                                            {...restField}
                                            name={[name, "title"]}
                                            rules={[{ required: true, message: "Başlık gerekli" }]}
                                            style={{ marginBottom: '6px' }}
                                        >
                                            <Input placeholder="Başlık" />
                                        </Form.Item>

                                        <Form.Item
                                            {...restField}
                                            name={[name, "type"]}
                                            rules={[{ required: true, message: "Tip gerekli" }]}
                                            style={{ marginBottom: '6px' }} // Form.Item arası boşluk azaltıldı
                                        >
                                            <Input placeholder="Tip (örn: important_questions, keywords)" />
                                        </Form.Item>

                                        <Form.Item
                                            {...restField}
                                            name={[name, "items"]}
                                            style={{ marginBottom: '0px' }}
                                        >
                                            <Select mode="tags" placeholder="Eleman ekle" />
                                        </Form.Item>
                                    </div>
                                ))}

                                <Form.Item>
                                    <Button
                                        type="dashed"
                                        onClick={() => add({ type: "", title: "", items: [] })}
                                        block
                                        icon={<PlusOutlined />}
                                    >
                                        Yeni Featured Alan Ekle
                                    </Button>
                                </Form.Item>
                            </div>
                        )}
                    </Form.List>
                    <Form.Item
                        label="İçerik"
                        name="content"
                        rules={[{ required: true, message: "İçerik gerekli" }]}
                    >
                        <ContentEditor />
                    </Form.Item>
                </Form>
            </ConfigProvider>
        </div >
    );
};